import { toast } from "react-toastify";
import apiClient from "./apiClient";

/**
 * Send OTP to a mobile number.
 * @param {string} mobileNumber - Mobile number to receive OTP.
 * @returns {Promise<any>}
 */
export const sendOtp = async (mobileNumber: string): Promise<any> => {
  try {
    const response = await apiClient.post("/sign-on/otp", { mobileNumber });

    console.log("OTP Sent:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to send OTP:", error);
    throw error;
  }
};

/**
 * Verify OTP for mobile authentication.
 * @param {string} mobileNumber - The mobile number used.
 * @param {string} otp - The OTP code.
 * @returns {Promise<any>}
 */
export const verifyOtp = async (mobileNumber: string, otp: string): Promise<any> => {
  try {
    const response = await apiClient.post("/sign-on/otp/verify", { mobileNumber, otp });

    if (response.status === 200) {
      console.log("OTP Verified:", response.data);
      return response.data;
    } else {
      toast.error("Invalid OTP. Please try again.");
      return { success: false, message: "Invalid OTP" };
    }
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw error;
  }
};

/**
 * Send Aadhaar OTP.
 * @param {string} aadhaar - Aadhaar number.
 */
export const sendAadharOtp = async (aadhaar: string) => {
  try {
    if (!aadhaar || aadhaar.length !== 12) {
      toast.error("Please enter a valid 12-digit Aadhaar number.");
      return;
    }

    const response = await apiClient.post("/KYC/aadhaar-send-otp", { aadhaar });

    if (response.data.message === "Success") {
      toast.success("OTP sent successfully!");
      return response.data;
    } else {
      toast.error(response.data.message || "Error sending OTP.");
    }
  } catch (error) {
    console.error("Error sending Aadhaar OTP:", error);
    toast.error("An error occurred. Please try again later.");
  }
};

/**
 * Verify Aadhaar OTP.
 * @param {string} aadhaar - Aadhaar number.
 * @param {string} otp - OTP code.
 * @param {string} requestId - Unique request ID.
 */
export const verifyAadharOtp = async (aadhaar: string, otp: string, requestId: string) => {
  try {
    if (!otp) {
      toast.error("Incorrect OTP. Please try again.");
      return;
    }

    const response = await apiClient.post("/KYC/verify-aadhaar-otp", { aadhaar, requestId, otp });
    return response.data;
  } catch (error) {
    console.error("Error verifying Aadhaar OTP:", error);
    toast.error("An error occurred while verifying OTP. Please try again.");
  }
};

/**
 * Verify Bank Details.
 * @param {string} accountNumber - Bank account number.
 * @param {string} ifsc - IFSC code.
 * @returns {Promise<any>}
 * @throws {Error}
 * @example
 * const response = await verifyBankDetails("1234567890", "ABCD1234");
**/

export const verifyBankDetails = async (accountNumber: string, ifsc: string): Promise<any> => {
  try {
    console.log("accountNumber", accountNumber);
    const response = await apiClient.post("/KYC/bank-details", { account_number: accountNumber, ifsc });

    if (response.data.message === "Success") {
      return { success: true, data: response.data };
    } else if (response.data.message === "Fraud activity detected for this account.") {
      toast.error("Fraud activity detected for this account.");
      return { success: false, message: "Fraud activity detected" };
    } else {
      toast.error("Invalid Bank Details");
      return { success: false, message: "Invalid Bank Details" };
    }
  } catch (error) {
    console.error("Error verifying bank details:", error);
    toast.error("An error occurred while verifying bank details. Please try again.");
    throw error;
  }
};

/**
 * Fetch GSTIN details.
 * @param {string} gst_no - GSTIN number.
 * @returns {Promise<any>}
 * @throws {Error}
 * @example
 * const response = await fetchGST("1234567890");
**/

export const fetchGST = async (gst_no: string): Promise<any> => {
  try {
    const response = await apiClient.post("/KYC/gst-details", { gst_no });
    return response.data;
  } catch (error) {
    console.error("Error fetching GSTIN:", error);
    throw error;
  }
};


/** * Fetch Udyam details.
 * @param {string} udhyamNumber - Udyam number.
 * @returns {Promise<any>}
 * @throws {Error}
 * @example
 * const response = await fetchUdhyamDetails("UDYAM-AB-01-2345678");
**/

export const fetchUdhyamDetails = async (udyam_number: string): Promise<any> => {
  try {
    const response = await apiClient.post("/KYC/udhyam", {udyam_number });
    return response.data;
  } catch (error) {
    console.error("Error fetching Udyam details:", error);
    throw error;
  }
}